import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Bold, Italic, Underline, Eraser } from 'lucide-react';

// Whitelist of inline tags we accept. Everything else is stripped.
const ALLOWED = new Set(['STRONG', 'B', 'EM', 'I', 'U', 'BR']);

const sanitizeNode = (node) => {
  // Walk children depth-first; unwrap or remove anything not whitelisted.
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      continue;
    }
    sanitizeNode(child);
    if (child.tagName === 'DIV' || child.tagName === 'P') {
      // Replace block wrappers with their contents + a <br> separator
      while (child.firstChild) node.insertBefore(child.firstChild, child);
      node.insertBefore(document.createElement('br'), child);
      child.remove();
      continue;
    }
    if (!ALLOWED.has(child.tagName)) {
      while (child.firstChild) node.insertBefore(child.firstChild, child);
      child.remove();
      continue;
    }
    for (const attr of Array.from(child.attributes)) {
      child.removeAttribute(attr.name);
    }
  }
};

const sanitizeHtml = (html) => {
  const wrap = document.createElement('div');
  wrap.innerHTML = html || '';
  sanitizeNode(wrap);
  return wrap.innerHTML.replace(/(<br\s*\/?>\s*)+$/i, '').trim();
};

// Public helper used by display components to render stored HTML safely.
export const renderSafeHtml = (html) => ({ __html: sanitizeHtml(html || '') });

// Strip tags entirely (for places that need plain text, e.g. search/export).
export const stripHtml = (html) => {
  const wrap = document.createElement('div');
  wrap.innerHTML = html || '';
  sanitizeNode(wrap);
  return (wrap.textContent || '').replace(/\s+/g, ' ').trim();
};

const ToolbarButton = ({ active, onMouseDown, label, children, testid }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
    title={label}
    data-testid={testid}
    className={`px-2 py-1 rounded text-xs border transition-colors ${
      active
        ? 'bg-amber-100 border-amber-400 text-amber-900'
        : 'bg-white border-[#E8DFCF] text-[#412402] hover:bg-amber-50'
    }`}
  >
    {children}
  </button>
);

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'اكتب هنا…',
  rows = 3,
  testid,
}) => {
  const ref = useRef(null);
  const [active, setActive] = useState({ bold: false, italic: false, underline: false });

  // Sync external value into the contentEditable only when it differs — avoids
  // wiping the caret on every keystroke.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = sanitizeHtml(value || '');
    if (el.innerHTML !== incoming) {
      el.innerHTML = incoming;
    }
  }, [value]);

  const refreshActive = useCallback(() => {
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    } catch { /* IE legacy */ }
  }, []);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = sanitizeHtml(el.innerHTML);
    onChange?.(next);
  }, [onChange]);

  const exec = useCallback((cmd) => {
    document.execCommand(cmd, false, null);
    refreshActive();
    emit();
    ref.current?.focus();
  }, [emit, refreshActive]);

  const clearFormatting = useCallback(() => {
    document.execCommand('removeFormat', false, null);
    refreshActive();
    emit();
    ref.current?.focus();
  }, [emit, refreshActive]);

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain') || '';
    document.execCommand('insertText', false, text);
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === 'b') { e.preventDefault(); exec('bold'); }
      else if (k === 'i') { e.preventDefault(); exec('italic'); }
      else if (k === 'u') { e.preventDefault(); exec('underline'); }
    }
  };

  const minHeightPx = Math.max(rows, 2) * 24;

  return (
    <div className="border border-[#E8DFCF] rounded-md overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-[#E8DFCF] bg-[#FAFAFA]">
        <ToolbarButton
          active={active.bold}
          onMouseDown={() => exec('bold')}
          label="عريض (Ctrl+B)"
          testid={testid ? `${testid}-bold` : undefined}
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          active={active.italic}
          onMouseDown={() => exec('italic')}
          label="مائل (Ctrl+I)"
          testid={testid ? `${testid}-italic` : undefined}
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          active={active.underline}
          onMouseDown={() => exec('underline')}
          label="تحته خط (Ctrl+U)"
          testid={testid ? `${testid}-underline` : undefined}
        >
          <Underline className="w-3.5 h-3.5" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-[#E8DFCF]" />
        <ToolbarButton
          active={false}
          onMouseDown={clearFormatting}
          label="إزالة التنسيق"
          testid={testid ? `${testid}-clear` : undefined}
        >
          <Eraser className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        dir="auto"
        onInput={emit}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        onFocus={refreshActive}
        data-testid={testid}
        data-placeholder={placeholder}
        className="rte-editable px-3 py-2 text-sm text-[#412402] outline-none whitespace-pre-wrap focus:bg-white"
        style={{ minHeight: `${minHeightPx}px` }}
      />
      <style>{`
        .rte-editable:empty:before {
          content: attr(data-placeholder);
          color: rgba(65, 36, 2, 0.35);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
